import {isAxiosError} from 'axios';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
	fetchIdentificationInfo,
	useUpdateCitizenship,
	useUpdateDocument,
	useUpdateIdentificationInfo,
	useUpdateInn,
	useUpdatePassportPhotos,
	useUpdateResidence,
	useUpdateSnils,
	useVerifyIdentity,
} from '@rocket-ui/lk-api';
import {useUserInteraction} from '@rocket-ui/user-interaction';
import {ROUTES} from 'app/router/routes';
import {
	buildCitizenshipRequest,
	buildDocumentsFlowPassportFormData,
	hasNewDocumentPhotos,
	buildDocumentsFlowResidenceFormData,
	buildForeignDocumentRequest,
	buildIdentificationInfoRequest,
	buildInnRequest,
	buildPassportDocumentRequest,
	buildResidenceCodeRequest,
	buildResidenceRequest,
	buildSnilsRequest,
	buildTaxIdentifierRequest,
	extractIdentificationDocumentPhotos,
	extractInnDigits,
	extractRegistrationBackendFlags,
	readSavedSnilsDigits,
	getNextDocumentsStep,
	INN_ALREADY_EXISTS_ERROR,
	isDocumentDetailsStepValid,
	isDocumentPhotosStepValid,
	isPersonalInfoStepValid,
	isResidenceDocumentStepValid,
	RegistrationStep,
	shouldSkipIdentityVerification,
	requiresMyTaxConnectionFlow,
	WORK_STATUS_STEP_ENABLED,
} from 'features/registration';
import {getInnApiErrorMessage, getRegistrationApiErrorMessage, getSnilsApiErrorMessage} from './helpers';
import type {RegistrationDerived} from './useRegistrationDerived';
import type {RegistrationPaymentFlow} from './useRegistrationPaymentFlow';
import type {StatusConnectionFlow} from './useStatusConnectionFlow';
import type {RegistrationWizardState} from './useRegistrationWizardState';

type Params = {
	state: RegistrationWizardState;
	derived: RegistrationDerived;
	statusConnection: StatusConnectionFlow;
	payment: RegistrationPaymentFlow;
	clearIdentityEditSnapshot: () => void;
	handleClose: () => void;
};

/** Сохранение данных шагов на бэк и переход к следующему шагу по кнопке «Продолжить». */
export const useRegistrationSubmit = ({
	state,
	derived,
	statusConnection,
	payment,
	clearIdentityEditSnapshot,
	handleClose,
}: Params) => {
	const navigate = useNavigate();
	const {notifyUser} = useUserInteraction();
	const [snilsError, setSnilsError] = useState<string | null>(null);
	const {mutateAsync: updateIdentificationInfo} = useUpdateIdentificationInfo();
	const {mutateAsync: updateCitizenship} = useUpdateCitizenship();
	const {mutateAsync: updateResidence} = useUpdateResidence();
	const {mutateAsync: updateDocument} = useUpdateDocument();
	const {mutateAsync: updateInn} = useUpdateInn();
	const {mutateAsync: updatePassportPhotos} = useUpdatePassportPhotos();
	const {mutateAsync: updateSnils} = useUpdateSnils();
	const {mutateAsync: verifyIdentity} = useVerifyIdentity();

	const {
		backendFlags,
		documentsFlow,
		documentsUpload,
		foreignDocumentDetails,
		identityEditSnapshot,
		innStep,
		isSubmitting,
		passportDetails,
		personalInfo,
		registrationFlow,
		residentCodeStep,
		returnToIdentityReview,
		setBackendFlags,
		setHasInnSaved,
		setHasSnilsSaved,
		setIdentificationDocumentPhotos,
		setInnError,
		setInnStep,
		setIsSubmitting,
		setResidentCodeStep,
		setReturnToIdentityReview,
		setSnilsStep,
		setStep,
		snilsStep,
		step,
		taxIdentifierStep,
		workStatus,
	} = state;

	const {
		isCurrentStepValid,
		isDocumentsFlowActive,
		isPaymentFlowActive,
		passportStepValidationOptions,
		combinesDocumentPhotosWithDetails,
		usesForeignDocumentDetails,
	} = derived;

	const refreshIdentificationDocumentPhotos = async () => {
		try {
			const {data} = await fetchIdentificationInfo();
			setIdentificationDocumentPhotos(extractIdentificationDocumentPhotos(data));
		} catch {
			// Превью с бэка не критичны для перехода на следующий шаг.
		}
	};

	const refreshIdentificationProfile = async () => {
		const {data} = await fetchIdentificationInfo();
		setBackendFlags(extractRegistrationBackendFlags(data));
		setIdentificationDocumentPhotos(extractIdentificationDocumentPhotos(data));

		const info = data?.info && typeof data.info === 'object' ? data.info : null;
		const inn = info && 'inn' in info ? info.inn : null;
		const savedSnils = readSavedSnilsDigits(info);

		if (typeof inn === 'string' && extractInnDigits(inn).length > 0) {
			setHasInnSaved(true);
			setInnStep({inn: extractInnDigits(inn)});
		}

		if (savedSnils) {
			setHasSnilsSaved(true);
			setSnilsStep({snils: savedSnils});
		}
	};

	const skipIdentityVerification = shouldSkipIdentityVerification(backendFlags);

	/** Актуальный флаг с бэка: если в стейте его ещё нет, читаем GET /identification/info. */
	const resolveSkipIdentityVerification = async (): Promise<boolean> => {
		if (typeof backendFlags.identityVerificationRequired === 'boolean') {
			return skipIdentityVerification;
		}

		try {
			const {data} = await fetchIdentificationInfo();
			const flags = extractRegistrationBackendFlags(data);
			setBackendFlags(flags);

			return shouldSkipIdentityVerification(flags);
		} catch {
			return skipIdentityVerification;
		}
	};

	/** Фото с профиля: при required=false уходим в профиль без POST identity_verification. */
	const finishProfilePhotosEdit = async () => {
		if (!(await resolveSkipIdentityVerification())) {
			await verifyIdentity();
		}

		navigate(ROUTES.profile, {replace: true});
	};

	/** false — сохранить ИНН не удалось, текст ошибки с бэка показан у поля. */
	const saveOptionalInn = async (request: {inn: string}): Promise<boolean> => {
		try {
			await updateInn(request);
			return true;
		} catch (error) {
			setInnError(getInnApiErrorMessage(error));
			return false;
		}
	};

	const handlePersonalInfoContinue = async () => {
		if (!isPersonalInfoStepValid(personalInfo)) {
			return;
		}

		const infoRequest = buildIdentificationInfoRequest(personalInfo);
		const citizenshipRequest = buildCitizenshipRequest(personalInfo);
		const residenceRequest = buildResidenceRequest(personalInfo);

		if (!infoRequest || !citizenshipRequest || !residenceRequest) {
			return;
		}

		const personalInnRequest =
			returnToIdentityReview && !usesForeignDocumentDetails ? buildInnRequest(innStep) : null;

		setIsSubmitting(true);

		try {
			await updateIdentificationInfo(infoRequest);
			await updateCitizenship(citizenshipRequest);
			await updateResidence(residenceRequest);

			if (personalInnRequest && !(await saveOptionalInn(personalInnRequest))) {
				return;
			}

			await refreshIdentificationProfile();

			if (returnToIdentityReview) {
				clearIdentityEditSnapshot();
				setReturnToIdentityReview(false);
				setStep(RegistrationStep.IdentityReview);
				return;
			}

			setStep(
				usesForeignDocumentDetails && !combinesDocumentPhotosWithDetails
					? RegistrationStep.DocumentPhotos
					: RegistrationStep.DocumentDetails,
			);
		} catch (error) {
			notifyUser(getRegistrationApiErrorMessage(error, 'Не удалось сохранить данные'));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDocumentsContinue = async () => {
		if (step === RegistrationStep.DocumentPhotos) {
			if (!isDocumentPhotosStepValid(documentsFlow, documentsUpload)) {
				return;
			}

			setIsSubmitting(true);

			try {
				if (hasNewDocumentPhotos(documentsFlow.passport.slots, documentsUpload)) {
					await updatePassportPhotos(buildDocumentsFlowPassportFormData(documentsFlow, documentsUpload));
					await refreshIdentificationDocumentPhotos();
				}

				if (returnToIdentityReview) {
					const fromProfilePhotosEdit = !identityEditSnapshot;

					clearIdentityEditSnapshot();
					setReturnToIdentityReview(false);

					if (fromProfilePhotosEdit) {
						await finishProfilePhotosEdit();
						return;
					}

					setStep(RegistrationStep.IdentityReview);
					return;
				}

				setStep(getNextDocumentsStep(step));
			} catch (error) {
				notifyUser(
					getRegistrationApiErrorMessage(
						error,
						error instanceof Error ? error.message : 'Не удалось загрузить фото документов',
					),
				);
			} finally {
				setIsSubmitting(false);
			}

			return;
		}

		if (step === RegistrationStep.ResidenceDocument) {
			if (!isResidenceDocumentStepValid(documentsFlow, documentsUpload)) {
				return;
			}

			setIsSubmitting(true);

			try {
				if (documentsUpload.slots.residenceDocument?.photo) {
					await updatePassportPhotos(
						buildDocumentsFlowResidenceFormData(documentsFlow, documentsUpload),
					);
					await refreshIdentificationDocumentPhotos();
				}

				if (documentsFlow.residence?.requireAddress && documentsUpload.residenceAddress.trim()) {
					await updateIdentificationInfo({
						placeOfResidence: documentsUpload.residenceAddress.trim(),
					});
				}

				if (returnToIdentityReview) {
					clearIdentityEditSnapshot();
					setReturnToIdentityReview(false);
				}

				setStep(RegistrationStep.IdentityReview);
			} catch (error) {
				notifyUser(
					getRegistrationApiErrorMessage(
						error,
						error instanceof Error ? error.message : 'Не удалось загрузить фото документов',
					),
				);
			} finally {
				setIsSubmitting(false);
			}

			return;
		}

		if (step !== RegistrationStep.DocumentDetails) {
			return;
		}

		if (!isDocumentDetailsStepValid(passportStepValidationOptions)) {
			return;
		}

		if (
			combinesDocumentPhotosWithDetails &&
			!isDocumentPhotosStepValid(documentsFlow, documentsUpload)
		) {
			return;
		}

		const documentRequest = usesForeignDocumentDetails
			? buildForeignDocumentRequest(
					personalInfo.citizenshipOksmCode,
					foreignDocumentDetails,
					personalInfo.residenceOksmCode,
				)
			: buildPassportDocumentRequest(passportDetails, personalInfo.citizenshipOksmCode);

		if (!documentRequest) {
			notifyUser('Проверьте паспортные данные');
			return;
		}

		setIsSubmitting(true);

		try {
			if (
				combinesDocumentPhotosWithDetails &&
				hasNewDocumentPhotos(documentsFlow.passport.slots, documentsUpload)
			) {
				await updatePassportPhotos(buildDocumentsFlowPassportFormData(documentsFlow, documentsUpload));
				await refreshIdentificationDocumentPhotos();
			}

			await updateDocument(documentRequest);

			if (returnToIdentityReview) {
				const fromProfilePhotosEdit = !identityEditSnapshot;

				clearIdentityEditSnapshot();
				setReturnToIdentityReview(false);

				if (fromProfilePhotosEdit) {
					await finishProfilePhotosEdit();
					return;
				}

				setStep(RegistrationStep.IdentityReview);
				return;
			}

			setStep(getNextDocumentsStep(step));
		} catch (error) {
			notifyUser(
				getRegistrationApiErrorMessage(
					error,
					error instanceof Error ? error.message : 'Не удалось сохранить паспортные данные',
				),
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const saveTaxStep = async () => {
		if (registrationFlow.taxIdType === 'russian_inn') {
			const innRequest = buildInnRequest(innStep);

			if (!innRequest) {
				return;
			}

			await updateInn(innRequest);
			setHasInnSaved(true);
			await refreshIdentificationProfile();

			return;
		}

		const taxIdentifierRequest = buildTaxIdentifierRequest(taxIdentifierStep);

		if (!taxIdentifierRequest) {
			return;
		}

		// Иностранный Tax ID тоже сохраняем через PATCH /identification/inn.
		await updateInn({inn: taxIdentifierRequest.identificationNumber});
		setHasInnSaved(true);
		await refreshIdentificationProfile();
	};

	const saveSnilsStep = async () => {
		const snilsRequest = buildSnilsRequest(snilsStep);

		if (!snilsRequest) {
			return;
		}

		await updateSnils(snilsRequest);
		setHasSnilsSaved(true);
	};

	const saveResidentCodeStep = async () => {
		const residenceCodeRequest = buildResidenceCodeRequest(residentCodeStep);

		if (!residenceCodeRequest) {
			return;
		}

		await updateIdentificationInfo(residenceCodeRequest);
	};

	const handleResidentCodeSkip = async () => {
		if (isSubmitting) {
			return;
		}

		setResidentCodeStep({code: '', skipped: true});
		setIsSubmitting(true);

		try {
			statusConnection.goAfterStatusDocuments();
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleIdentificationInnContinue = async () => {
		if (isSubmitting) {
			return;
		}

		const taxIdentifierRequest = buildTaxIdentifierRequest(taxIdentifierStep);
		const innRequest =
			registrationFlow.taxIdType === 'foreign_tax_id'
				? taxIdentifierRequest
					? {inn: taxIdentifierRequest.identificationNumber}
					: null
				: buildInnRequest(innStep);

		if (!innRequest) {
			return;
		}

		setInnError(null);
		setIsSubmitting(true);

		try {
			if (!(await saveOptionalInn(innRequest))) {
				return;
			}

			setHasInnSaved(true);
			await refreshIdentificationProfile();
			setStep(
				documentsFlow.residence ? RegistrationStep.ResidenceDocument : RegistrationStep.IdentityReview,
			);
		} catch (error) {
			if (isAxiosError(error) && error.response?.status === 400) {
				setInnError(INN_ALREADY_EXISTS_ERROR);
				return;
			}

			setInnError(getInnApiErrorMessage(error));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleContinue = async () => {
		if (!isCurrentStepValid || isSubmitting) {
			return;
		}

		if (isPaymentFlowActive) {
			payment.handlePaymentContinue();
			return;
		}

		if (isDocumentsFlowActive) {
			await handleDocumentsContinue();
			return;
		}

		if (step === RegistrationStep.IdentificationInn) {
			await handleIdentificationInnContinue();
			return;
		}

		if (step === RegistrationStep.IdentityReview) {
			setIsSubmitting(true);

			try {
				if (await resolveSkipIdentityVerification()) {
					if (WORK_STATUS_STEP_ENABLED) {
						setStep(RegistrationStep.WorkStatus);
					} else {
						handleClose();
					}

					return;
				}

				await verifyIdentity();
				navigate(ROUTES.profile, {replace: true});
			} catch (error) {
				notifyUser(getRegistrationApiErrorMessage(error, 'Не удалось подтвердить данные'));
			} finally {
				setIsSubmitting(false);
			}

			return;
		}

		if (WORK_STATUS_STEP_ENABLED && step === RegistrationStep.WorkStatus) {
			setStep(RegistrationStep.RegistrationComplete);
			return;
		}

		if (step === RegistrationStep.PersonalInfo) {
			await handlePersonalInfoContinue();
			return;
		}

		setIsSubmitting(true);

		try {
			if (step === RegistrationStep.Inn) {
				await saveTaxStep();

				if (requiresMyTaxConnectionFlow(workStatus)) {
					const bound = await statusConnection.bindSelfEmploymentPartnerAfterInn();

					if (!bound) {
						return;
					}
				}

				await statusConnection.goAfterTaxStep();
				return;
			}

			if (step === RegistrationStep.Snils) {
				await saveSnilsStep();
				statusConnection.goAfterSnilsStep();
				return;
			}

			if (step === RegistrationStep.ResidentCode) {
				await saveResidentCodeStep();
				statusConnection.goAfterStatusDocuments();
			}
		} catch (error) {
			if (step === RegistrationStep.Inn && isAxiosError(error) && error.response?.status === 400) {
				setInnError(INN_ALREADY_EXISTS_ERROR);
			}

			if (step === RegistrationStep.Snils) {
				setSnilsError(getSnilsApiErrorMessage(error));
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return {
		handleContinue,
		handleResidentCodeSkip,
		snilsError,
		closeSnilsError: () => setSnilsError(null),
	};
};
